/* eslint-disable new-cap */
import { z } from '@seek/indie-api-types';
import type { FormConfig, FormField } from './types.ts';

const VALIDATION_MESSAGES = {
  REQUIRED: (label: string) => `${label} is required`,
  INVALID_EMAIL: 'Please enter a valid email address',
  INVALID_URL: 'Please enter a valid URL',
  INVALID_PHONE: 'Please enter a valid phone number',
  INVALID_OPTION: 'Please select a valid option',
  INVALID_OPTIONS: 'Please select only valid options',
  INVALID_DATE: 'Please enter a valid date',
  FUTURE_DATE: 'Please select a future date',
  PAST_DATE: 'Please select a past date',
} as const;

// TODO: Make this betterer
const PHONE_REGEX = /^\+?[\d\s\-\(\)]+$/;

const isEmpty = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0);

type ZodFields =
  | z.ZodString
  | z.ZodNumber
  | z.ZodArray<z.ZodString>
  | z.ZodArray<z.ZodNumber>;

const createBaseZodType = (type: FormField['type']): ZodFields => {
  switch (type) {
    case 'string':
      return z.string();
    case 'number':
      return z.number();
    case 'stringArray':
      return z.array(z.string());
    case 'numberArray':
      return z.array(z.number());
    default:
      throw new Error(`Unsupported type: ${type}`);
  }
};

const createNumberValidation = (
  field: FormField,
  zodType: z.ZodNumber,
): z.ZodNumber => {
  let result = zodType;
  if ('min' in field && field.min !== undefined) {
    result = result.min(field.min);
  }
  if ('max' in field && field.max !== undefined) {
    result = result.max(field.max);
  }
  return result;
};

const createTextValidation = (
  field: FormField,
  zodType: z.ZodString,
): z.ZodString => {
  let result = zodType;
  if ('minLength' in field && field.minLength !== undefined) {
    result = result.min(field.minLength);
  }
  if ('maxLength' in field && field.maxLength !== undefined) {
    result = result.max(field.maxLength);
  }
  return result;
};

const createSelectValidation = (field: FormField) => {
  if (!('options' in field)) {
    throw new Error('Select field must have options');
  }
  const allowedValues = field.options.map((option) => option.value);
  if (field.type === 'string') {
    return z.enum(allowedValues as [string, ...string[]]);
  } else if (field.type === 'number') {
    return z.number().refine((val) => allowedValues.includes(val), {
      message: VALIDATION_MESSAGES.INVALID_OPTION,
    });
  }
  throw new Error(`Unsupported select type: ${field.type}`);
};

const createMultiSelectValidation = (field: FormField) => {
  if (!('options' in field)) {
    throw new Error('MultiSelect field must have options');
  }
  const allowedValues = field.options.map((option) => option.value);
  if (field.type === 'stringArray') {
    return z.array(z.enum(allowedValues as [string, ...string[]]));
  } else if (field.type === 'numberArray') {
    return z
      .array(z.number())
      .refine((arr) => arr.every((val) => allowedValues.includes(val)), {
        message: VALIDATION_MESSAGES.INVALID_OPTIONS,
      });
  }
  throw new Error(`Unsupported multiselect type: ${field.type}`);
};

const createDateValidation = (field: FormField): z.ZodString => {
  let zodType = z.string().refine(
    (dateString) => {
      const date = new Date(dateString);
      return !isNaN(date.getTime());
    },
    { message: VALIDATION_MESSAGES.INVALID_DATE },
  );
  if ('future' in field && field.future) {
    zodType = zodType.refine(
      (dateString) => new Date(dateString) > new Date(),
      { message: VALIDATION_MESSAGES.FUTURE_DATE },
    );
  } else if ('past' in field && field.past) {
    zodType = zodType.refine(
      (dateString) => new Date(dateString) < new Date(),
      { message: VALIDATION_MESSAGES.PAST_DATE },
    );
  }
  return zodType;
};

const checkDependencies = (
  dependsOn: NonNullable<FormField['dependsOn']>,
  formData: Record<string, unknown>,
): boolean => {
  const { logicalOperator, dependencies } = dependsOn;
  const results = dependencies.map((dep) => {
    const fieldValue = formData[dep.id];
    return fieldValue === dep.value;
  });
  return logicalOperator === 'and'
    ? results.every(Boolean)
    : results.some(Boolean);
};

const applyInputTypeValidation = (field: FormField, baseType: ZodFields) => {
  if (!('inputType' in field)) {
    return baseType;
  }
  switch (field.inputType) {
    case 'Email':
      return z.email(VALIDATION_MESSAGES.INVALID_EMAIL);
    case 'Url':
      return z.url(VALIDATION_MESSAGES.INVALID_URL);
    case 'Phone':
      return z.string().regex(PHONE_REGEX, VALIDATION_MESSAGES.INVALID_PHONE);
    case 'Number':
      return createNumberValidation(field, baseType as z.ZodNumber);
    case 'TextField':
    case 'TextArea':
      return createTextValidation(field, baseType as z.ZodString);
    case 'Select':
      return createSelectValidation(field);
    case 'MultiSelect':
      return createMultiSelectValidation(field);
    case 'Date':
      return createDateValidation(field);
    default:
      throw new Error(`Unsupported input type in field: ${field}`);
  }
};

export const buildFormSchema = (formConfig: FormConfig) => {
  const schemaObject = formConfig.reduce(
    (acc: Record<string, z.ZodType<unknown>>, field: FormField) => {
      const baseType = createBaseZodType(field.type);
      const validatedType = applyInputTypeValidation(field, baseType);
      if (field.required && !field.dependsOn) {
        acc[field.id] = validatedType;
      } else {
        acc[field.id] = validatedType.optional();
      }
      return acc;
    },
    {},
  );

  return z.object(schemaObject).check((ctx) => {
    formConfig.forEach((field) => {
      const { id, required, dependsOn, label } = field;
      const fieldValue = ctx.value[id];
      const shouldValidate = dependsOn
        ? checkDependencies(dependsOn, ctx.value) && required
        : required;

      if (shouldValidate && isEmpty(fieldValue)) {
        ctx.issues.push({
          input: fieldValue,
          code: 'custom',
          message: VALIDATION_MESSAGES.REQUIRED(label),
          path: [id],
        });
      }
    });
  });
};
